import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { CategoryTile } from '../ui/CategoryTile'
import { LoginCta } from '../ui/LoginCta'
import { LoyaltyCard } from '../ui/LoyaltyCard'
import { PullToRefresh } from '../ui/PullToRefresh'
import { ProductCard } from '../ui/ProductCard'
import { Failed, Loading } from '../ui/Load'
// Старые истории «Что нового» пока скрыты — вернуть вместе с <Stories /> ниже.
// import { Stories } from '../features/home/Stories'
import { StoriesShelf } from '../features/stories'
import { NewsShelf } from '../features/news'
import { env } from '../config/env'
import { useSession } from '../app/session'
import { useCategories, usePopular } from '../features/catalog/api'
import { useLoyalty } from '../features/account/api'
import type { OrdersFrom } from '../features/account/OrdersScreen'
import { useCart } from '../features/cart/CartProvider'
import { keys } from '../api/keys'
import { shelf } from './homeModel'

const POPULAR_ON_HOME = 6

export function HomePage() {
  const { t, i18n } = useTranslation(['home', 'common'])
  const navigate = useNavigate()
  const { isAuthenticated } = useSession()
  const cart = useCart()

  const categories = useCategories()
  const popular = usePopular(i18n.language)
  const loyalty = useLoyalty(isAuthenticated)
  const client = useQueryClient()

  /** Потянули главную вниз: баланс, полки, сторис и новости — заново. */
  const refresh = () =>
    Promise.all([
      isAuthenticated ? loyalty.refetch() : null,
      categories.refetch(),
      popular.refetch(),
      client.invalidateQueries({ queryKey: keys.stories }),
      client.invalidateQueries({ queryKey: keys.news }),
    ])

  return (
    <section className="screen home-screen">
      <PullToRefresh onRefresh={refresh} />
      <h1 className="visually-hidden">{t('common:app.client')}</h1>
      {/* Сторис — первыми, над картой: так их видно, не прокручивая. Новая
          полка из админки — за флагом VITE_FEATURE_STORIES; без флага главная
          такая же, как раньше: старые истории скрыты (вернуть — раскомментировать
          <Stories /> ниже и импорт). */}
      {env.features.stories ? <StoriesShelf /> : null}
      {/* <Stories /> */}
      <div className="sect">
        {isAuthenticated ? (
          <LoyaltyCard
            balance={loyalty.data?.balance ?? 0}
            onHistory={() => {
              void navigate('/orders?tab=points', {
                state: { fromHome: true } satisfies OrdersFrom,
              })
            }}
          />
        ) : (
          <LoginCta
            onSignIn={() => {
              void navigate('/login')
            }}
          />
        )}
      </div>

      {/* Новости — над категориями, за флагом VITE_FEATURE_NEWS. */}
      {env.features.news ? <NewsShelf /> : null}

      <div className="sect">
        <div className="sect-head">
          <h2>{t('home:sections.categories')}</h2>
          <button
            type="button"
            onClick={() => {
              void navigate('/categories')
            }}
          >
            {t('home:sections.allCategories')}
          </button>
        </div>
        {categories.isPending ? <Loading what={t('common:state.loading')} shape="tiles" /> : null}
        {categories.isError ? (
          <Failed
            error={categories.error}
            onRetry={() => {
              void categories.refetch()
            }}
          />
        ) : null}
        <div className="cattiles">
          {(categories.data ?? []).map((category) => (
            <CategoryTile
              key={category.id}
              category={category}
              onOpen={(id) => {
                void navigate(`/catalog?cat=${id}`)
              }}
            />
          ))}
        </div>
      </div>

      <div className="sect">
        <div className="sect-head">
          <h2>{t('home:sections.popular')}</h2>
          <button
            type="button"
            onClick={() => {
              void navigate('/catalog')
            }}
          >
            {t('home:sections.allProducts')}
          </button>
        </div>
        {popular.isPending ? <Loading what={t('common:state.loading')} shape="products" /> : null}
        {popular.isError ? (
          <Failed
            error={popular.error}
            onRetry={() => {
              void popular.refetch()
            }}
          />
        ) : null}
        <div className="grid">
          {shelf(popular.data ?? [], POPULAR_ON_HOME).map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              quantity={cart.quantityOf(product.id)}
              onOpen={(id) => {
                void navigate(`/catalog/${id}`)
              }}
              onAdd={() => {
                cart.changeQuantity(product.id, 1, product.maxQty)
              }}
              onChangeQuantity={(_product, delta) => {
                cart.changeQuantity(product.id, delta, product.maxQty)
              }}
            />
          ))}
        </div>
      </div>
    </section>
  )
}
